import {
    AbstractTCITenantComponentOptions,
    ITCIApplicationRequest,
    ITCIApplicationResponse,
    ITCIOptionType,
    ITCITenantComponentOptions,
    TCITenantSelectorType
} from "./tci-tenant";
import { ITCIAppConfiguration, ITCIAppProperty, TCITenantAppType } from "./tci-application";
import { Observable, Subject } from "rxjs";
import { TCIActionValidationResult } from "./tci-validation";
import { ITCIVpnConnectionBrief } from "./tci-vpn";
import { strEnum } from "../../types";


/**
 * Enumeration of TCI app detail env menu types
 */
export const TCI_APP_DETAIL_ENV_MENU_TYPE: ITCIOptionType<string> = {
    MAIN_MENU: "main",
    HAMBURGER_MENU: "hamburger"
};
export type TCI_APP_DETAIL_ENV_MENU_TYPE = keyof typeof TCI_APP_DETAIL_ENV_MENU_TYPE;


export class TCITenantAppDetailEnvMenu
    extends AbstractTCITenantComponentOptions<TCI_APP_DETAIL_ENV_MENU_TYPE>
    implements ITCITenantComponentOptions<TCI_APP_DETAIL_ENV_MENU_TYPE> {

    public static TCI_MENU_APP_DETAILS_ENV_APP_VARS = new TCITenantAppDetailEnvMenu(
        "Application variables",
        TCITenantSelectorType.TCI_APP_DETAIL_ENV_APP_VARS, 1, "appvars", [
        TCITenantAppType.flogo,
        TCITenantAppType.bw,
        TCITenantAppType.node
    ], TCI_APP_DETAIL_ENV_MENU_TYPE.MAIN_MENU);

    public static TCI_MENU_APP_DETAILS_ENV_ENG_VARS = new TCITenantAppDetailEnvMenu(
        "Engine variables",
        TCITenantSelectorType.TCI_APP_DETAIL_ENV_ENG_VARS, 2, "engvars", [
        TCITenantAppType.flogo,
        TCITenantAppType.bw,
        TCITenantAppType.node
    ], TCI_APP_DETAIL_ENV_MENU_TYPE.MAIN_MENU);

    public static TCI_MENU_APP_DETAILS_ENV_APP_VPN = new TCITenantAppDetailEnvMenu(
        "VPN connections",
        TCITenantSelectorType.TCI_APP_DETAIL_ENV_APP_VPN, 3, "appvpn", [
        TCITenantAppType.flogo,
        TCITenantAppType.bw,
        TCITenantAppType.node
    ], TCI_APP_DETAIL_ENV_MENU_TYPE.MAIN_MENU);

    public static TCI_MENU_APP_DETAILS_ENV_HYBRID_CONN = new TCITenantAppDetailEnvMenu(
        "Hybrid connectivity",
        TCITenantSelectorType.TCI_APP_DETAIL_ENV_HYBRID_CONN, 4, "hybridconn", [
        TCITenantAppType.flogo,
        TCITenantAppType.bw,
        TCITenantAppType.node
    ], TCI_APP_DETAIL_ENV_MENU_TYPE.MAIN_MENU);

    public static TCI_MENU_APP_DETAILS_ENV_AUTO_SCALE = new TCITenantAppDetailEnvMenu(
        "Autoscaling",
        TCITenantSelectorType.TCI_APP_DETAIL_ENV_AUTO_SCALE, 5, "autoscale", [
        TCITenantAppType.flogo,
        TCITenantAppType.bw,
        TCITenantAppType.node
    ], TCI_APP_DETAIL_ENV_MENU_TYPE.MAIN_MENU);

    private static allItems = [
        TCITenantAppDetailEnvMenu.TCI_MENU_APP_DETAILS_ENV_APP_VARS,
        TCITenantAppDetailEnvMenu.TCI_MENU_APP_DETAILS_ENV_APP_VPN,
        TCITenantAppDetailEnvMenu.TCI_MENU_APP_DETAILS_ENV_ENG_VARS,
        TCITenantAppDetailEnvMenu.TCI_MENU_APP_DETAILS_ENV_HYBRID_CONN,
        TCITenantAppDetailEnvMenu.TCI_MENU_APP_DETAILS_ENV_AUTO_SCALE,
    ];

    /**
     * Gets All menu items
     * @param menuType
     * @return @ITCITenantMenu[]
     */
    public static getAllMenuItems(menuType?: TCI_APP_DETAIL_ENV_MENU_TYPE): ITCITenantComponentOptions<TCI_APP_DETAIL_ENV_MENU_TYPE>[] {
        if (menuType) {
            return TCITenantAppDetailEnvMenu.allItems.filter((v) => {
                return v.getOptionType() === menuType;
            });
        }
        return TCITenantAppDetailEnvMenu.allItems;
    }

    /**
     * constructor
     * @param menuOption
     * @param componentSelectorType
     * @param position
     * @param routeId
     * @param tenantAppTypes
     * @param menuType
     */
    constructor(menuOption: string,
        componentSelectorType: TCITenantSelectorType,
        position: number,
        routeId: string,
        tenantAppTypes: TCITenantAppType[],
        menuType: TCI_APP_DETAIL_ENV_MENU_TYPE = TCI_APP_DETAIL_ENV_MENU_TYPE.MAIN_MENU) {
        super(menuOption, componentSelectorType, position, menuType, routeId, tenantAppTypes, null);
    }
}


/**
 * TCI_ENV_CTRL_ACTION_TYPE
 * @type {{[K in string]: K}}
 */
export const TCI_ENV_CTRL_ACTION_TYPE = strEnum([

    // Env Controls
    "APP_PROPERTIES",
    "ENGINE_PROPERTIES",
    "HYBRID_CONNECTIVITY",
    "APP_VPN",
    "APP_AUTO_SCALE",
    "REFRESH"
]);
export type TCI_ENV_CTRL_ACTION_TYPE = keyof typeof TCI_ENV_CTRL_ACTION_TYPE;

export interface ITCIAppEnvControlProviderRequest extends ITCIApplicationRequest {
    actionType: TCI_ENV_CTRL_ACTION_TYPE;
    // TODO: Add UI data structure here based of DS output
    data?: ITCIAppConfiguration;
    autoScaleConfig?: ITCIAppAutoScaleConfig;
    isOnPremise?: boolean;
}

export interface ITCIAppAutoScaleConfig {
    /**
     * autoScale
     */
    autoScaleEnabled: boolean;
    lowLoadFactor: number;
    highLoadFactor: number;
}

export interface ITCIAppEnvControlProviderResponse extends ITCIApplicationResponse {
    actionType: TCI_ENV_CTRL_ACTION_TYPE;
    /**
     * Warnings that may emerge during application env ctrls.
     * If there are elements then the warnings list would be displayed.
     */
    warnings?: TCIActionValidationResult;

    // TODO: Add UI data structure here based of DS output
    data?: ITCIAppConfiguration;
    /**
     * global access key list
     */
    accessKeyList?: string[];
    /**
     * access key value of application
     */
    defaultAccessKey?: string;
    /**
     * global vpn connection list
     */
    vpnList?: ITCIVpnConnectionBrief[];
    /**
     * vpn value of application
     */
    defaultVPNConnectionId?: string;

    /**
     * autoScale
     */
    autoScaleConfig?: ITCIAppAutoScaleConfig;
}

export interface ITCIApplicationAutoScaleUpdateRequest extends ITCIApplicationRequest {
    autoScaleConfig: ITCIAppAutoScaleConfig;
}
export interface ITCIApplicationAutoScaleUpdateResponse extends ITCIApplicationResponse {
    autoScaleConfig: ITCIAppAutoScaleConfig;
}

/**
 *
 */
export class ApplicationEnvCtrlResponse extends TCIActionValidationResult implements ITCIAppEnvControlProviderResponse {
    /**
     * Application Id
     */
    appId: string;
    /**
    * Update Action Type
    */
    actionType: TCI_ENV_CTRL_ACTION_TYPE;
    /**
     * Application Name
     */
    name: string;
    /**
     * Application Response status
     */
    success: boolean;
    /**
     * Application Response message
     */
    msg?: string;

    // TODO: Add UI data structure here based of DS output
    data: any;
}

export interface ITCIAppEnvControlProvider {
    /**
     * store tab data
     */
    setTabData(key: TCI_ENV_CTRL_ACTION_TYPE, data: any): void;

    /**
     * Returns a Subject, the UI consumer will subscribe and unsubscribe from this
     * subject when the UI is active and inactive respectively. The provider's responsibility is to
     * notify changes on the environment controls configuration. On every change the verification methods
     * canPushApplicationProperties, canPushEngineProperties, canPushHybridConnectivity and canPushAppVPNConnection
     * will be executed to get the states
     * @return Subject<null>
     */
    getStatusSubject(): Subject<TCI_ENV_CTRL_ACTION_TYPE>;

    /**
     * initialize the component
     */
    initApplicationProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    canPushApplicationProperties(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle app properties update
     * @param request
     */
    pushApplicationProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * initialize the component
     */
    initEngineProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    canPushEngineProperties(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle engine properties update
     * @param request
     */
    pushEngineProperties(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * initialize the component
     */
    initHybridConnectivity(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    canPushHybridConnectivity(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle hybrid connectivity update
     * @param request
     */
    pushHybridConnectivity(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * initialize the component
     */
    initAppVPNConnection(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    canPushAppVPNConnection(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle app vpn update
     * @param request
     */
    pushAppVPNConnection(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * initialize AutoScale component
     */
    initAppAutoScale(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;

    /**
     * check if there is an update
     */
    canPushAppAutoScale(request: ITCIAppEnvControlProviderRequest): Observable<boolean>;

    /**
     * handle app autoscale update
     * @param request
     */
    pushAppAutoScale(request: ITCIAppEnvControlProviderRequest): Observable<ITCIAppEnvControlProviderResponse>;
}

export interface ITCIAppConfigDetails {
    appProperties: ITCIAppProperty[];
    systemProperties: ITCIAppProperty[];
}
export interface ITCIApplicationConfiguration {
    configDetails: ITCIAppConfigDetails;
}
